import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useGallery, usePropertyField, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  altText?: string;
  keywords?: string[];
  pageHint?: string;
}

const SLOT_GROUP_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PageHero({
  tokenPrefix,
  title = 'Page',
  altText = 'Page banner',
  keywords = [],
  pageHint = '',
}: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const bgPath = `${prefix}.bg_image`;

  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');
  const titleValue = useContentToken(titlePath, { defaultValue: title });
  const bgImage = useSlotGroup(bgPath, SLOT_GROUP_DEF);

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  return (
    <section className="relative h-[30vh] min-h-[200px] max-h-[350px] overflow-hidden">
      <EditableSlotGroup
        groupPath={bgPath}
        definition={SLOT_GROUP_DEF}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            url={displayImage}
            alt={bgImage.alt || altText}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/40" />
      </EditableSlotGroup>

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center text-white">
        <EditableToken
          path={titlePath}
          as="h1"
          className="mb-3 font-heading text-3xl font-normal uppercase tracking-wider sm:text-4xl md:text-5xl"
        >
          {titleValue || title}
        </EditableToken>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <EditableToken path="pagehero.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableSlotGroup
        groupPath="pagehero.bg_image"
        definition={SLOT_GROUP_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Page',
    },
  },
  slotGroups: {
    'pagehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
